import {useLayoutEffect, useRef, useState} from 'react';
import {RichTextBlock} from './richTextBlock';

const DEFAULT_MAX_HEIGHT = 300;

interface CollapsibleRichTextProps {
  html: string;
  className?: string;
  maxHeight?: number;
  onExpand?: () => void;
}

/**
 * Renders rich-text HTML inside a container that collapses with a
 * gradient fade and a "Read more" / "Show less" toggle when the
 * content exceeds `maxHeight` (defaults to 300 px).
 *
 * TODO: Make fade color configurable with props
 */
export function CollapsibleRichText({
  html,
  className,
  maxHeight = DEFAULT_MAX_HEIGHT,
  onExpand,
}: CollapsibleRichTextProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > maxHeight);
    }
  }, [maxHeight]);

  return (
    <div>
      <div className="relative">
        <div
          ref={contentRef}
          className={!isExpanded && isClamped ? 'overflow-hidden' : undefined}
          style={!isExpanded && isClamped ? {maxHeight} : undefined}
        >
          <RichTextBlock html={html} className={className} />
        </div>
        {isClamped && !isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-neutral-50 via-neutral-50/80 to-transparent pointer-events-none" />
        )}
      </div>
      {isClamped && (
        <button
          type="button"
          onClick={() => {
            if (!isExpanded && onExpand) onExpand();
            setIsExpanded(!isExpanded);
          }}
          className="relative cursor-pointer z-10 mt-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          {isExpanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}
